import { HttpMethod, TaskResponse } from '@/interfaces/task'
import { config } from '@/config'
import { logger } from '@/lib'
import { CookieKeys, CookieService } from '@/services/cookie'

export class TaskService {
  private static readonly API_URL = config.api.baseUrl

  private static prepareUrl(url: string): string {
    if (!url.startsWith('/')) {
      return `/${url}`
    }
    return url
  }

  private static async handleResponse(response: Response): Promise<TaskResponse> {
    const jsonResponse = await response.json()
    const { status } = response
    const token: string | null = response.headers.get('x-auth-token')
    return {
      code: status,
      result: jsonResponse,
      token,
    }
  }

  private static async fetchWithTimeout(url: string, init: object | undefined, timeoutDuration = 30_000): Promise<any> {
    const controller = new AbortController()
    const timeout = setTimeout(() => {
      controller.abort()
      throw new Error('Request timeout')
    }, timeoutDuration)
    try {
      return await fetch(url, { ...init, signal: controller.signal })
    } finally {
      clearTimeout(timeout)
    }
  }

  public static async execute({
    url,
    method,
    body,
    isMultipart = false,
  }: {
    url: string
    method: HttpMethod
    body?: any
    isMultipart?: boolean
    timeoutDuration?: number
  }): Promise<TaskResponse> {
    const targetUrl = `${this.API_URL}${this.prepareUrl(url)}`
    const token = CookieService.getItem(CookieKeys.RegistrantToken) || ''

    const headers = {
      'x-access-token': token,
    }
    if (method !== HttpMethod.GET && body && !isMultipart) {
      body = JSON.stringify(body)
      Object.assign(headers, { 'Content-Type': 'application/json' })
    } else if (!isMultipart) {
      body = null
    }
    try {
      const isServer = typeof window === 'undefined'
      const init: any = { method, headers, body }
      if (isServer) {
        init.cache = 'no-store'
      }
      const response = await this.fetchWithTimeout(targetUrl, init)
      return this.handleResponse(response)
    } catch (error) {
      logger.error(error)
      return {
        code: 500,
        result: [(error as Error).message || error, null],
        token: null,
        isNetworkError: true,
      }
    }
  }
}
